/**
 * Moving a rest deadline forward or back.
 *
 * Pulled out of RestTimer because it is the whole behaviour of the ±15s buttons
 * and it is all edge cases: an expired deadline, a subtraction that runs past
 * zero, a deadline that was never set. None of them are reachable from a test
 * while the arithmetic is tangled up with a ref, an interval and a notification.
 *
 * The rule that matters: anchor to `max(deadline, now)`, never to the deadline
 * alone. An expired deadline is in the past, so adding to it bought less than
 * the button promised — and once the timer had sat at 0:00 for longer than the
 * increment, the new deadline was *still* in the past, so the display flashed
 * the added seconds, the tick pulled it straight back to 0:00, and the "rest
 * over" buzz fired again for a press that achieved nothing.
 */

export type RestShift =
  /** The rest is over: subtracting took it to or past zero. */
  | { kind: 'end' }
  /** Keep counting to `deadline`, showing `remaining` seconds. */
  | { kind: 'run'; deadline: number; remaining: number }

export function shiftDeadline(opts: {
  /** Current deadline in epoch ms, or null if the timer never started. */
  deadline: number | null
  now: number
  /** Seconds to add; negative to take off. */
  delta: number
}): RestShift {
  const base = Math.max(opts.deadline ?? 0, opts.now)
  const deadline = base + opts.delta * 1000
  const remaining = Math.round((deadline - opts.now) / 1000)

  // Rounding matters at the boundary: 500ms left and −15s applied lands at
  // −14.5s, which must end the rest rather than report a negative clock.
  if (remaining <= 0) return { kind: 'end' }

  return { kind: 'run', deadline, remaining }
}
